import { supabase } from "@/integrations/supabase/client";

const CATALOG_QUERY_FIELDS = `
  *,
  opportunity:opportunities(*),
  npo:npo_profiles(*),
  images:opportunity_catalog_images(*)
`;

export interface OpportunityFilters {
  page?: number;
  limit?: number;
  search?: string;
  beneficiaryTypes?: string[];
  causeAreas?: string[];
  relevantUNDays?: string[];
  sdgs?: string[];
}

export const getCatalogById = async (token: string, id: string | undefined) => {
  if (!id) throw new Error("Catalog ID is required");

  return await getCatalog(token, {}, id)
};

export const getCatalog = async (token: string, filters: OpportunityFilters = {}, idFilter?: string) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  const { page = 1, limit = 10, search, beneficiaryTypes, sdgs, relevantUNDays } = filters;
  const params = new URLSearchParams();
  if (idFilter) {
    params.append('id', idFilter);
  }
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  if (search) {
    params.append('search', search);
  }
  
  if (beneficiaryTypes?.length) {
    params.append('beneficiaryTypes', JSON.stringify(beneficiaryTypes));
  }
  
  if (relevantUNDays?.length) {
    params.append('day', JSON.stringify(relevantUNDays));
  }
  
  if (sdgs?.length) {
    params.append('sdgs', JSON.stringify(sdgs));
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch opportunities');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching opportunities:', error);
    throw error;
  }
};


export const getOpportunities = async (token: string, filters: OpportunityFilters = {}, idFilter?: string) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  const { page = 1, limit = 10, search, beneficiaryTypes, sdgs, causeAreas } = filters;
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  if (search) {
    params.append('search', search);
  }
  
  if (beneficiaryTypes?.length) {
    params.append('beneficiaryTypes', JSON.stringify(beneficiaryTypes));
  }
  
  if (idFilter) {
    params.append('id', idFilter);
  }

  if (sdgs?.length) {
    params.append('sdgs', JSON.stringify(sdgs));
  }

  if (causeAreas?.length) {
    params.append('types', JSON.stringify(causeAreas));
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch opportunities');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching opportunities:', error);
    throw error;
  }
};

export const getRecommendedOpportunities = async (
  token: string,
  filters: {
    beneficiary_types?: string[];
    focus_areas?: string[];
    sdg_alignment?: string[];
  }
) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  let query = supabase
    .from("opportunities")
    .select("*");

  // Add filter conditions if arrays are provided and not empty
  if (filters.beneficiary_types?.length) {
    query = query.overlaps('beneficiary_types', filters.beneficiary_types);
  }

  if (filters.focus_areas?.length) {
    query = query.overlaps('focus_areas', filters.focus_areas);
  }

  if (filters.sdg_alignment?.length) {
    query = query.overlaps('sdg_alignment', filters.sdg_alignment);
  }

  // Order by relevance score (number of matching filters)
  query = query.order('created_at', { ascending: false });

  const { data, error } = await query;

  if (error) throw error;

  // Calculate relevance score for each catalog entry
  const scoredData = data.map(entry => {
    let score = 0;

    if (filters.beneficiary_types?.length) {
      score += entry.beneficiary_types.filter(type => 
        filters.beneficiary_types?.includes(type)
      ).length;
    }

    if (filters.focus_areas?.length) {
      score += entry.focus_areas.filter(area => 
        filters.focus_areas?.includes(area)
      ).length;
    }

    if (filters.sdg_alignment?.length) {
      score += entry.sdg_alignment.filter(sdg => 
        filters.sdg_alignment?.includes(sdg)
      ).length;
    }

    return { ...entry, relevance_score: score };
  });

  // Sort by relevance score (highest first)
  return scoredData.sort((a, b) => b.relevance_score - a.relevance_score);
};

export const createOpportunity = async (token: string, data: any) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create opportunity');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating opportunity:', error);
    throw error;
  }
};

export const updateOpportunity = async (token: string, id: string, data: any) => {
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog/${id}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update opportunity');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating opportunity:', error);
    throw error;
  }
};
